/**
 * Angular Checkbox Component
 *
 * This is a migrated version of the AngularJS acme-checkbox component.
 * It follows the same migration pattern as AlertComponent.
 *
 * Original: apps/b2b-ui/common/components/checkbox/checkbox.component.ts
 *
 * It implements ControlValueAccessor so it works with both template-driven
 * (`[(ngModel)]`) and reactive (`formControl`) Angular forms — the idiomatic
 * replacement for the AngularJS `require: { ngModelCtrl: 'ngModel' }` pattern.
 * It also exposes a `checked` two-way binding for use outside of forms.
 *
 * Usage in Angular templates:
 *   <app-checkbox [(ngModel)]="accepted">I accept the terms</app-checkbox>
 *   <app-checkbox [(checked)]="value" [indeterminate]="partial">Select all</app-checkbox>
 *   <app-checkbox formControlName="newsletter">Subscribe</app-checkbox>
 *
 * Usage in AngularJS templates (after downgrade):
 *   <ng-checkbox checked="$ctrl.value" disabled="$ctrl.disabled">Label</ng-checkbox>
 */
import { Component, EventEmitter, HostBinding, Input, Output, computed, signal, forwardRef } from '@angular/core';
import { NgClass } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

let uniqueId = 0;

@Component({
  selector: 'app-checkbox',
  standalone: true,
  imports: [NgClass],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CheckboxComponent),
      multi: true,
    },
  ],
  template: require('./checkbox.component.html'),
  styles: [require('./checkbox.component.less')],
  host: {
    '[class.acme-checkbox--active]': 'active()',
  },
})
export class CheckboxComponent implements ControlValueAccessor {
  /** Whether the checkbox is disabled */
  @Input() disabled = false;

  private readonly _indeterminate = signal(false);
  /** Renders the indeterminate (mixed) state, regardless of `checked` */
  @Input()
  get indeterminate(): boolean { return this._indeterminate(); }
  set indeterminate(value: boolean) { this._indeterminate.set(value); }

  private readonly _checked = signal(false);
  /** Checked state — supports `[(checked)]` two-way binding outside of forms */
  @Input()
  get checked(): boolean { return this._checked(); }
  set checked(value: boolean) { this._checked.set(value); }
  @Output() checkedChange = new EventEmitter<boolean>();

  /** Unique id linking the <input> for label/aria purposes */
  readonly id = `acme-checkbox-${++uniqueId}`;

  /** ngAria adds a host tabindex; we delegate focus to the real input instead. */
  @HostBinding('attr.tabindex') readonly tabindex = -1;
  @HostBinding('class.acme-checkbox') readonly baseClass = true;

  readonly active = computed(() => this._checked() || this._indeterminate());

  private onChange: (value: boolean) => void = () => {};
  onTouched: () => void = () => {};

  onCheckboxChange(event: Event): void {
    const newValue = (event.target as HTMLInputElement).checked;
    this._checked.set(newValue);
    this.checkedChange.emit(newValue);
    this.onChange(newValue);
  }

  // ── ControlValueAccessor ──────────────────────────────────────────────────

  writeValue(value: boolean): void {
    this._checked.set(!!value);
  }

  registerOnChange(fn: (value: boolean) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
